/**
 * Form State Management Service
 *
 * Centralizes all form state management including ESAL modal state, form dirty tracking,
 * design-only change detection, and material cascade integration with intelligent caching.
 *
 * This service extracts scattered form state logic from InputForm component for improved
 * testability, maintainability, and performance through centralized state management.
 */

import { DynamicFormikProps } from '../types/FormTypes';
import { logDebug, logError } from '../utils/utils';
import { FormStateMachine, FormState as StateMachineState, FormEvent } from './FormStateMachine';
import { selectEsalTrucks, selectEsalSummaryInputs } from '../Dynamic/store/project/esalSlice';
import { redux } from '../../reduxService';
import { setHasBeenRan, setGeogridHasBeenRan, selectDynamicProject } from '../Dynamic/store/project/projectslice';
import { EProjectType } from '../enum/project';

// Types and interfaces
export interface FormState {
    isCustomDirty: boolean;
    isDesignChange: boolean;
    isSubmittingDesign: boolean;
}

export interface EsalModalState {
    isOpen: boolean;
    modalKey: number;
    formikRef: DynamicFormikProps | null;
}

interface CachedFormData {
    formState: FormState;
    esalModalState: EsalModalState;
    materialCascadeRef: React.RefObject<unknown> | null;
    timestamp: number;
}

interface FormStateChangeListener {
    (formState: FormState): void;
}

interface EsalModalChangeListener {
    (esalModalState: EsalModalState): void;
}

interface FormStateEvent {
    type: 'formStateUpdate' | 'esalModalOpen' | 'esalModalClose' | 'materialCascadeInit' | 'designChange';
    payload: Record<string, unknown>;
    timestamp: number;
}

/**
 * Serializes TruckData[] to minimal format for API/storage
 * Extracts only user-configurable fields, omits derived and static data
 */
const serializeTrucksForSave = (trucks: any[]) => {
    return trucks.map(truck => ({
        id: truck.id,
        selected: truck.selected,
        direction1Vehicles: truck.direction1Vehicles,
        direction2Vehicles: truck.direction2Vehicles,
        passagePercentage: truck.passagePercentage,
        customTotalLoad: truck.customTotalLoad,
    }));
};

/**
 * Centralized service for form state management with intelligent caching
 */
/**
 * Centralized service for form state management with intelligent caching
 */
export class FormStateManagementService {
    private static instance: FormStateManagementService;

    // 🚀 NEW: State machine replaces complex boolean logic (now public for tests)
    public readonly stateMachine: FormStateMachine;

    // 🔄 Legacy: Computed form state for backward compatibility
    private get formState(): FormState {
        const machineState = this.stateMachine.getCurrentState();
        return {
            isCustomDirty: !this.stateMachine.isClean(),
            isDesignChange: this.stateMachine.isDesignDirty(),
            isSubmittingDesign: this.stateMachine.isSubmitting(),
        };
    }

    private esalModalState: EsalModalState = {
        isOpen: false,
        modalKey: 0,
        formikRef: null,
    };

    private materialCascadeRef: React.RefObject<unknown> | null = null;

    // Cache and performance
    private cache = new Map<string, CachedFormData>();
    private readonly CACHE_TTL = 2 * 60 * 1000; // 2 minutes for form state (shorter than other services)
    private cacheTimestamps = new Map<string, number>();
    private cleanupTimer?: NodeJS.Timeout;

    // Event system
    private formStateListeners: Set<FormStateChangeListener> = new Set();
    private esalModalListeners: Set<EsalModalChangeListener> = new Set();

    private constructor() {
        this.setupCacheCleanup();

        // 🚀 Initialize state machine
        this.stateMachine = new FormStateMachine();

        // Subscribe to state machine changes for legacy compatibility
        this.stateMachine.subscribe(() => {
            this.notifyFormStateListeners();
        });

        logDebug('[FormStateManagementService]', 'Service initialized with State Machine Pattern');
    }

    /**
     * Get singleton instance of FormStateManagementService
     */
    public static getInstance(): FormStateManagementService {
        if (!FormStateManagementService.instance) {
            FormStateManagementService.instance = new FormStateManagementService();
        }
        return FormStateManagementService.instance;
    }

    /**
     * Setup automatic cache cleanup timer
     */
    private setupCacheCleanup(): void {
        this.cleanupTimer = setInterval(() => {
            this.cleanupExpiredCache();
        }, this.CACHE_TTL / 2); // Clean every minute
    }

    /**
     * Clean up expired cache entries
     */
    private cleanupExpiredCache(): void {
        const now = Date.now();
        let cleanedCount = 0;

        // Cleanup main cache
        const cacheEntries = Array.from(this.cacheTimestamps.entries());
        for (const [key, timestamp] of cacheEntries) {
            if (now - timestamp > this.CACHE_TTL) {
                this.cache.delete(key);
                this.cacheTimestamps.delete(key);
                cleanedCount++;
            }
        }

        if (cleanedCount > 0) {
            logDebug('[FormStateManagementService]', `Cache cleanup completed: ${cleanedCount} entries removed`);
        }
    }

    /**
     * Get cached data if available and valid
     */
    private getCachedData(key: string): CachedFormData | null {
        const cached = this.cache.get(key);
        const timestamp = this.cacheTimestamps.get(key);

        if (cached && timestamp && Date.now() - timestamp < this.CACHE_TTL) {
            logDebug('[FormStateManagementService]', `Cache hit for: ${key}`);
            return cached;
        }

        return null;
    }

    /**
     * Cache data with timestamp
     */
    private setCachedData(key: string, data: CachedFormData): void {
        const now = Date.now();
        this.cache.set(key, { ...data, timestamp: now });
        this.cacheTimestamps.set(key, now);
    }

    /**
     * Notify form state listeners
     */
    private notifyFormStateListeners(): void {
        this.formStateListeners.forEach(listener => {
            try {
                listener({ ...this.formState });
            } catch (error) {
                logError('[FormStateManagementService]', `Error notifying form state listener: ${error}`);
            }
        });
    }

    /**
     * Notify ESAL modal listeners
     */
    private notifyEsalModalListeners(): void {
        this.esalModalListeners.forEach(listener => {
            try {
                listener({ ...this.esalModalState });
            } catch (error) {
                logError('[FormStateManagementService]', `Error notifying ESAL modal listener: ${error}`);
            }
        });
    }

    /**
     * Log form state events for debugging
     */
    private logFormStateEvent(event: FormStateEvent): void {
        logDebug('[FormStateManagementService]', `${event.type.toUpperCase()}: ${JSON.stringify(event.payload)}`);
    }

    // ===== FORM STATE MANAGEMENT =====

    /**
     * Get current form state
     */
    public getFormState(): FormState {
        const cacheKey = 'current_form_state';
        const cached = this.getCachedData(cacheKey);

        if (cached) {
            return cached.formState;
        }

        // Cache current state
        this.setCachedData(cacheKey, {
            formState: this.formState,
            esalModalState: this.esalModalState,
            materialCascadeRef: this.materialCascadeRef,
            timestamp: Date.now(),
        });

        return { ...this.formState };
    }

    // 🚀 NEW: State machine enhanced methods

    /**
     * Check if form should trigger a redirect (non-design changes)
     */
    public canRedirect(): boolean {
        return this.stateMachine.canRedirect();
    }

    /**
     * Check if form should auto-submit for recalculation (design changes)
     */
    public shouldAutoSubmit(): boolean {
        return this.stateMachine.shouldAutoSubmit();
    }

    /**
     * Get current state machine state for debugging
     */
    public getStateMachineState(): string {
        return this.stateMachine.exportState();
    }

    /**
     * Force reset to clean state (emergency use)
     */
    public forceReset(): boolean {
        const success = this.stateMachine.reset();
        if (success) {
            this.cache.delete('current_form_state');
            logDebug('[FormStateManagementService]', 'Force reset to clean state');
        }
        return success;
    }

    /**
     * 🚀 ENHANCED Update form state with State Machine Pattern
     *
     * This method handles form state transitions using the predictable State Machine pattern.
     * No more complex boolean logic or preservation edge cases - just clean state transitions.
     *
     * 🆕 AUTO-RESETS hasBeenRan: When state becomes FORM_DIRTY (non-design changes),
     * hasBeenRan is automatically reset to false because the displayed results are no longer
     * valid for the current form values.
     */
    public updateFormState(dirty: boolean, values: any, initialValues: any): void {
        // 🚀 NEW: Use state machine instead of complex boolean logic
        const previousState = this.stateMachine.getCurrentState();

        logDebug('[FormStateManagementService]', 'updateFormState called', {
            dirty,
            valuesKeys: values ? Object.keys(values) : [],
            initialValuesKeys: initialValues ? Object.keys(initialValues) : [],
            currentState: previousState.current,
        });

        if (!dirty) {
            // Form is clean - only reset if not already in CLEAN state
            if (!this.stateMachine.isClean()) {
                this.stateMachine.reset();
            }
            // Clear cache when state changes
            this.cache.delete('current_form_state');
            logDebug('[FormStateManagementService]', 'Form reset to clean state');
            return;
        }

        // Form is dirty - determine if it's design-only or regular change
        const isDesignOnly = this.performDesignOnlyDetection(values, initialValues);
        const success = this.stateMachine.handleFormChange(isDesignOnly);

        if (success) {
            const newState = this.stateMachine.getCurrentState();
            logDebug('[FormStateManagementService]', 'State machine transition successful', {
                from: previousState.current,
                to: newState.current,
                isDesignOnly,
                canRedirect: newState.canRedirect,
                shouldAutoSubmit: newState.shouldAutoSubmit,
            });

            // 🆕 LOGIC FIX: Reset hasBeenRan when state becomes FORM_DIRTY
            // When user modifies a non-design field, the displayed results are no longer valid
            // for the current form values, so hasBeenRan must be reset to false
            if (newState.current === StateMachineState.FORM_DIRTY && previousState.current !== StateMachineState.FORM_DIRTY) {
                logDebug('[FormStateManagementService]', '🔄 Resetting hasBeenRan=false: form has non-design changes, results are no longer valid');
                redux.dispatch(setHasBeenRan(false));

                // 🔧 FIX: Also reset geogridHasBeenRan for projects with geogrid
                // REASON: GeoGrid form needs to redirect from /geogrid-results when results invalid
                // SCOPE: Only dispatch for projects that have geogrid capability (Paved/Airport/etc.)
                // This prevents unnecessary Redux updates for projects without geogrid
                const currentProject = redux.getState().Dynamic?.project?.data;
                if (currentProject?.projectInfo?.data?.geoGrid) {
                    logDebug('[FormStateManagementService]', '🔄 Resetting geogridHasBeenRan=false: project has geogrid data');
                    redux.dispatch(setGeogridHasBeenRan(false));
                }
            }

            // Clear cache when state changes
            this.cache.delete('current_form_state');
        } else {
            logError('[FormStateManagementService]', 'State machine transition failed', {
                currentState: previousState.current,
                isDesignOnly,
                dirty,
            });
        }
    }

    /**
     * Detect if changes are design-only (simplified - no cache needed with State Machine)
     */
    public isDesignOnlyChange(values: Record<string, unknown>, initialValues: Record<string, unknown>): boolean {
        return this.performDesignOnlyDetection(values, initialValues);
    }

    /**
     * 🔍 Core business logic: Determine if form changes are design-only
     *
     * **ProjectType-Aware Configuration (Fixed in v5.1.0):**
     * - Paved/Airport: Use design sections (surfaceDesign, baseDesign, subbaseDesign) + ignore projectSettings
     * - Container Yard/Railway/Unpaved: NO design sections, NO ignored fields (all fields are important)
     *
     * Returns true if ALL changes are within design sections, false otherwise.
     * This is the critical logic that determines whether redirect should be prevented.
     */
    private performDesignOnlyDetection(values: Record<string, unknown>, initialValues: Record<string, unknown>): boolean {
        if (!values || !initialValues) {
            console.log('🚫 [FORM_STATE] Missing values or initialValues');
            return false;
        }

        // 🆕 Get current projectType from Redux store
        const state = redux.getState();
        const dynamicProject = selectDynamicProject(state);
        const projectType = dynamicProject?.data?.projectInfo?.data?.type as EProjectType | undefined;

        console.log('🔧 [FORM_STATE] FormStateManagementService.performDesignOnlyDetection called with:', {
            projectType,
            valuesKeys: Object.keys(values),
            initialValuesKeys: Object.keys(initialValues),
        });

        // 🎯 BUSINESS LOGIC: Define what constitutes "design-only" changes based on projectType
        let designSections: string[] = [];
        let designThicknessFields: string[] = [];
        let ignoredFields: string[] = [];

        switch (projectType) {
            case EProjectType.paved:
            case EProjectType.airport:
                // PAVED/AIRPORT: Have design sections, projectSettings should be ignored
                designSections = ['surfaceDesign', 'baseDesign', 'subbaseDesign'];
                designThicknessFields = ['baseThickness', 'subbaseThickness', 'surfaceThickness'];
                ignoredFields = ['projectSettings']; // projectSettings changes automatically with design enable flags
                break;

            case EProjectType.container_yard:
            case EProjectType.parking_deck:
            case EProjectType.railways:
            case EProjectType.unpaved:
            case EProjectType.geogrid:
            default:
                // OTHER PROJECT TYPES: NO design sections, ALL fields are important
                // projectSettings.calculationMethod is a critical field that MUST trigger save
                designSections = [];
                designThicknessFields = [];
                ignoredFields = [];
                break;
        }

        console.log('📋 [FORM_STATE] Using config for', projectType, ':', {
            designSections,
            designThicknessFields,
            ignoredFields,
        });

        const getChangedPaths = (current: any, initial: any, path = ''): string[] => {
            const changes: string[] = [];

            if (typeof current !== 'object' || current === null || typeof initial !== 'object' || initial === null) {
                if (current !== initial) {
                    changes.push(path);
                }
                return changes;
            }

            const allKeys = new Set([...Object.keys(current), ...Object.keys(initial)]);
            for (const key of Array.from(allKeys)) {
                const newPath = path ? `${path}.${key}` : key;
                changes.push(...getChangedPaths(current[key], initial[key], newPath));
            }

            return changes;
        };

        const changedPaths = getChangedPaths(values, initialValues);
        console.log('📊 [FORM_STATE] Changed paths detected:', changedPaths);

        // Se non ci sono cambiamenti, return false
        if (changedPaths.length === 0) {
            console.log('🚫 [FORM_STATE] No changes detected');
            return false;
        }

        // Controlla se tutti i cambiamenti sono nelle sezioni di design O nei thickness fields
        const nonDesignChanges = changedPaths.filter(path => {
            // 🔧 Ignore fields that shouldn't trigger redirect (e.g., projectSettings for Paved/Airport)
            const isIgnored = ignoredFields.some(field => path === field || path.startsWith(`${field}.`));
            if (isIgnored) {
                console.log('✅ [FORM_STATE] Path ignored (not a non-design change):', path);
                return false; // Don't consider ignored fields as non-design changes
            }

            // Check se il path inizia con una design section
            const isInDesignSection = designSections.some(section => path.startsWith(section));

            // Check se il path è un thickness field dentro una design section
            const isDesignThicknessField = designSections.some(section => designThicknessFields.some(field => path === `${section}.${field}`));

            const isDesignChange = isInDesignSection || isDesignThicknessField;

            console.log('🔍 [FORM_STATE] Analyzing path:', {
                path,
                isInDesignSection,
                isDesignThicknessField,
                isDesignChange,
            });

            return !isDesignChange;
        });

        const result = changedPaths.length > 0 && nonDesignChanges.length === 0;

        console.log('✅ [FORM_STATE] FormStateManagementService final result:', {
            projectType,
            changedPathsCount: changedPaths.length,
            nonDesignChangesCount: nonDesignChanges.length,
            nonDesignChanges,
            result,
        });

        return result;
    }

    /**
     * Set submission state with safety timeout management
     */
    public setSubmittingDesign(isSubmitting: boolean): void {
        // 🚀 NEW: Use state machine for submission state
        if (isSubmitting) {
            const success = this.stateMachine.startSubmission();
            if (success) {
                logDebug('[FormStateManagementService]', 'Submission started');
                this.cache.delete('current_form_state');
            }
        } else {
            const success = this.stateMachine.completeSubmission();
            if (success) {
                logDebug('[FormStateManagementService]', 'Submission completed, form returned to clean state');
                this.cache.delete('current_form_state');
            }
        }
    }

    /**
     * Handles failed form submission (e.g. network error)
     * Transitions state machine from SUBMITTING back to FORM_DIRTY to allow retry
     */
    public submitFailed(): void {
        logError('FormStateManagementService', 'Form submission failed - recovering state machine');
        this.stateMachine.transition(FormEvent.SUBMIT_FAILED);
        // No need to notify listeners explicitly as transition() does it if state changes
        // But we might want to clear cache
        this.cache.delete('current_form_state');
    }

    // ===== ESAL MODAL MANAGEMENT =====

    /**
     * Get current ESAL modal state
     */
    public getEsalModalState(): EsalModalState {
        return { ...this.esalModalState };
    }

    /**
     * Open ESAL modal with formik reference
     */
    public openEsalModal(formikRef: DynamicFormikProps): void {
        this.esalModalState = {
            isOpen: true,
            modalKey: this.esalModalState.modalKey + 1, // Increment for new modal instance
            formikRef,
        };

        this.logFormStateEvent({
            type: 'esalModalOpen',
            payload: { modalKey: this.esalModalState.modalKey },
            timestamp: Date.now(),
        });

        this.cache.delete('current_form_state');
        this.notifyEsalModalListeners();
    }

    /**
     * Close ESAL modal with cleanup
     * Replicates handleEsalModalClose logic from InputForm
     */
    public closeEsalModal(): void {
        this.esalModalState = {
            isOpen: false,
            modalKey: this.esalModalState.modalKey + 1, // Increment for force remount
            formikRef: null,
        };

        this.logFormStateEvent({
            type: 'esalModalClose',
            payload: { newModalKey: this.esalModalState.modalKey },
            timestamp: Date.now(),
        });

        this.cache.delete('current_form_state');
        this.notifyEsalModalListeners();
    }

    /**
     * Handle ESAL calculation completion
     * Replicates handleEsalCalculationComplete logic from InputForm
     */
    public completeEsalCalculation(result: number | { esalFinalValue: number; totalPassagesPerYear: number }): boolean {
        // Can only complete if modal is open
        if (!this.esalModalState.isOpen) {
            logError('[FormStateManagementService]', 'Cannot complete ESAL calculation: modal is not open');
            return false;
        }

        try {
            if (this.esalModalState.formikRef?.setFieldValue) {
                // Handle both old number format and new object format
                if (typeof result === 'number') {
                    // Backward compatibility: manual mode returns just the number
                    this.esalModalState.formikRef.setFieldValue('trafficLoad.esalFinalValue', result);
                } else {
                    // New automatic mode: returns both values
                    // Get current Redux state for full persistence
                    const trucks = selectEsalTrucks(redux.getState());
                    const summaryInputs = selectEsalSummaryInputs(redux.getState());

                    // Save calculated final values
                    this.esalModalState.formikRef.setFieldValue('trafficLoad.esalFinalValue', result.esalFinalValue);
                    this.esalModalState.formikRef.setFieldValue('trafficLoad.totalPassagesPerYear', result.totalPassagesPerYear);
                    this.esalModalState.formikRef.setFieldValue('trafficLoad.esalCalculationMode', 'automatic');

                    // NEW: Save full modal state for reconstruction on reload
                    this.esalModalState.formikRef.setFieldValue('trafficLoad.esalTrucks', serializeTrucksForSave(trucks));
                    this.esalModalState.formikRef.setFieldValue('trafficLoad.esalSummaryInputs', summaryInputs);

                    logDebug('FormStateManagementService', 'Saved full ESAL configuration', {
                        trucksCount: trucks.filter((t: any) => t.selected).length,
                        summaryInputs,
                    });
                }
            }

            // Close modal after successful completion
            this.closeEsalModal();

            this.logFormStateEvent({
                type: 'esalModalClose',
                payload: { result, success: true },
                timestamp: Date.now(),
            });

            return true;
        } catch (error) {
            logError('[FormStateManagementService]', `Error completing ESAL calculation: ${error}`);
            return false;
        }
    }

    // ===== MATERIAL CASCADE MANAGEMENT =====

    /**
     * Get material cascade ref
     */
    public getMaterialCascadeRef(): React.RefObject<unknown> | null {
        return this.materialCascadeRef;
    }

    /**
     * Set material cascade ref
     */
    public setMaterialCascadeRef(ref: React.RefObject<unknown> | null): void {
        this.materialCascadeRef = ref;

        this.logFormStateEvent({
            type: 'materialCascadeInit',
            payload: { hasRef: !!ref },
            timestamp: Date.now(),
        });

        this.cache.delete('current_form_state');
    }

    // ===== EVENT SYSTEM =====

    /**
     * Subscribe to form state changes
     */
    public subscribeToFormState(listener: FormStateChangeListener): () => void {
        this.formStateListeners.add(listener);

        logDebug('[FormStateManagementService]', `Form state listener subscribed, total: ${this.formStateListeners.size}`);

        return () => {
            this.formStateListeners.delete(listener);
            logDebug('[FormStateManagementService]', `Form state listener unsubscribed, total: ${this.formStateListeners.size}`);
        };
    }

    /**
     * Subscribe to ESAL modal state changes
     */
    public subscribeToEsalModal(listener: EsalModalChangeListener): () => void {
        this.esalModalListeners.add(listener);

        logDebug('[FormStateManagementService]', `ESAL modal listener subscribed, total: ${this.esalModalListeners.size}`);

        return () => {
            this.esalModalListeners.delete(listener);
            logDebug('[FormStateManagementService]', `ESAL modal listener unsubscribed, total: ${this.esalModalListeners.size}`);
        };
    }

    // ===== UTILITY METHODS =====

    /**
     * Reset all form state (useful for new projects or testing)
     */
    public resetFormState(): void {
        // 🚀 NEW: Reset state machine only if not already in CLEAN state
        const currentState = this.stateMachine.getCurrentState();
        if (currentState.current !== 'CLEAN') {
            this.stateMachine.reset();
        }

        this.esalModalState = {
            isOpen: false,
            modalKey: 0,
            formikRef: null,
        };

        this.materialCascadeRef = null;

        // Clear all caches
        this.cache.clear();
        this.cacheTimestamps.clear();

        this.notifyFormStateListeners();
        this.notifyEsalModalListeners();

        logDebug('[FormStateManagementService]', 'All form state reset');
    }

    /**
     * Get cache statistics for debugging
     */
    public getCacheStats(): {
        mainCache: number;
        formStateListeners: number;
        esalModalListeners: number;
    } {
        return {
            mainCache: this.cache.size,
            formStateListeners: this.formStateListeners.size,
            esalModalListeners: this.esalModalListeners.size,
        };
    }

    /**
     * Cleanup method for service shutdown
     */
    public cleanup(): void {
        // Clear all timers
        if (this.cleanupTimer) {
            clearInterval(this.cleanupTimer);
            this.cleanupTimer = undefined;
        }

        this.cache.clear();
        this.cacheTimestamps.clear();
        this.formStateListeners.clear();
        this.esalModalListeners.clear();

        logDebug('[FormStateManagementService]', 'Service cleanup completed');
    }
}

// Export singleton instance for backward compatibility and ease of use
export const formStateManagementService = FormStateManagementService.getInstance();

// Export default for consistent import patterns
export default FormStateManagementService;
